/**
 * What one revision changed, for a change point.
 *
 * Nothing kind-specific lives here. `revisionSnapshot` already knows what is
 * worth diffing for each of the eight kinds, and `diffDefinitionRevisions`
 * already knows how to word a difference — both belong to the version history,
 * and reusing them is the point: a change point names a revision of a CHILD, and
 * a child's "Duration 45 → 60" must read exactly like the root's.
 *
 * The predecessor is diffed against the current revision ALONE, with no graph
 * composed in. A revision changes its own resource and nothing else, so folding
 * children in would report a parent as having changed when only something under
 * it moved — which is the very confusion change points exist to clear up.
 */

import type { RevisionPairResult } from "@repo/api";
import { type RevisionSnapshotKind, revisionSnapshot } from "../views/definition-revision-snapshot";
import { type DefinitionRevisionChange, diffDefinitionRevisions } from "../views/definition-version-diff";

/**
 * No predecessor means this revision created the lineage, which is a `created`
 * entry rather than an empty diff: "nothing changed" and "this is where it
 * began" are different answers.
 */
export function diffRevisionPair(pair: RevisionPairResult): DefinitionRevisionChange[] {
  const kind = pair.kind as RevisionSnapshotKind;
  if (!pair.previous) return [{ label: "Definition created", type: "created" }];
  return diffDefinitionRevisions(revisionSnapshot(kind, pair.previous), revisionSnapshot(kind, pair.current));
}
